// ACC-436: ticketing's coarse role is derived from Forecast's granular ticket
// permissions (forwarded by the gateway as ticket_perms, ACC-426). The ranking
// mirrors hasMinRole's ROLE_RANK (agent < lead < admin).

export const TICKET_PERMS = {
	VIEW: 'TICKETS_VIEW',
	CREATE: 'TICKETS_CREATE',
	ASSIGN: 'TICKETS_ASSIGN',
	MANAGE: 'TICKETS_MANAGE',
	ADMIN: 'TICKETS_ADMIN',
	// FOR-261: not a TICKETS_* tier — an orthogonal own-vs-others toggle, so it
	// deliberately takes no part in deriveRole below.
	ATTACHMENT_DELETE_ALL: 'ATTACHMENT_DELETE_ALL',
} as const;

type Role = 'agent' | 'lead' | 'admin';

/** Derive ticketing's coarse role from Forecast's granular ticket permissions. */
export function deriveRole(perms: readonly string[]): Role {
	if (perms.includes(TICKET_PERMS.ADMIN)) return 'admin';
	if (perms.includes(TICKET_PERMS.MANAGE)) return 'lead';
	return 'agent';
}

/** Convenience predicate for granular perm checks at call sites. */
export function hasTicketPerm(perms: readonly string[], perm: string): boolean {
	return perms.includes(perm);
}

// ACC-440: action → permission predicates. These are the single source of
// truth mapping a ticketing action to the granular Forecast permission the
// gateway (ACC-468) enforces, so client affordances never diverge from the
// server. Each fails closed (false) when the perm is absent.
export const canViewTickets = (perms: readonly string[]): boolean => hasTicketPerm(perms, TICKET_PERMS.VIEW);

export const canCreateTickets = (perms: readonly string[]): boolean => hasTicketPerm(perms, TICKET_PERMS.CREATE);

/** Bulk assign — gateway gates BulkAssignTickets on TICKETS_ASSIGN. */
export const canAssignTickets = (perms: readonly string[]): boolean => hasTicketPerm(perms, TICKET_PERMS.ASSIGN);

/** Any updateTicket-backed write (status change, single/board/inline assignee
 *  change, resolve) — gateway gates UpdateTicket on TICKETS_MANAGE. */
export const canManageTickets = (perms: readonly string[]): boolean => hasTicketPerm(perms, TICKET_PERMS.MANAGE);

export const isTicketsAdmin = (perms: readonly string[]): boolean => hasTicketPerm(perms, TICKET_PERMS.ADMIN);

/** FOR-261: delete an attachment someone ELSE uploaded — gateway gates
 *  DeleteAttachment on ATTACHMENT_DELETE_ALL. Deleting your own file needs no
 *  permission, so this is not the whole gate; see canDeleteAttachment. */
export const canDeleteOthersAttachments = (perms: readonly string[]): boolean =>
	hasTicketPerm(perms, TICKET_PERMS.ATTACHMENT_DELETE_ALL);

/**
 * FOR-261: may this viewer delete this specific attachment?
 *
 * Own files are always deletable; other people's need ATTACHMENT_DELETE_ALL. An
 * attachment with no resolvable uploader (inbound email, or a deactivated person)
 * can never match on ownership, so it falls through to the permission — which is
 * what the backend does too. Fails closed when the viewer's own id is unknown.
 */
export const canDeleteAttachment = (
	perms: readonly string[],
	viewerPersonId: string | null | undefined,
	uploaderPersonId: string | null | undefined
): boolean => {
	const isOwnFile = !!viewerPersonId && !!uploaderPersonId && viewerPersonId === uploaderPersonId;
	return isOwnFile || canDeleteOthersAttachments(perms);
};
